import { notNullOrUndefined, useUrlSearchParams } from '@dai/common'
import {
  Alert,
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid2,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import { DefaultParamType, TFnType } from '@tolgee/react'
import dayjs from 'dayjs'
import { cloneDeep } from 'lodash'
import { Calendar, Clock, Store } from 'lucide-react'
import { useMemo } from 'react'
import { useParams } from 'react-router-dom'

import CenteredBox from '@/components/CenteredBox'
import { DaiDivider } from '@/components/DaiDivider'
import { ProductInitialInfo } from '@/components/ProductDetailsCard'
import { RestockFragment, RestockGroupFragment, SnapshotType } from '@/graphql'
import useT from '@/i18n/useT'
import useProductsByUpc from '@/pages/coolers/hooks/useProductsByUpc'

import RestockSnapshotCard from './RestockSnapshotCard'
import {
  calculateRestockStats,
  formatDelta,
  formatStatChange,
  getSnapshotByType,
  useRestockGroupData,
  useTimingData,
} from './useRestockGroupDetail.logic'

const RestockHeader = ({
  restockGroup,
  timingData,
  t,
}: {
  restockGroup: RestockGroupFragment
  timingData: ReturnType<typeof useTimingData>
  t: TFnType<DefaultParamType, string, any>
}) => (
  <Paper elevation={1} sx={{ mb: 3 }}>
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h4"
            component="h1"
            fontWeight="bold"
            gutterBottom
          >
            {restockGroup.actionComplete?.action.title}
          </Typography>
          <Stack direction="row" spacing={3} sx={{ color: 'text.secondary' }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Store size={16} />
              <Typography variant="body2">
                {restockGroup.store?.location?.name}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Calendar size={16} />
              <Typography variant="body2">
                {timingData.dueDate
                  ? t('restockGroupDetail.dueDate', {
                      date: timingData.dueDate.toDate(),
                    })
                  : t('common.notApplicable')}
              </Typography>
            </Stack>
          </Stack>
        </Box>
        <Chip
          label={
            timingData.isOnTime
              ? t('restockGroupDetail.completedOnTime')
              : t('restockGroupDetail.completedLate', {
                  lateAmount: timingData.lateAmount,
                })
          }
          color={timingData.isOnTime ? 'success' : 'error'}
          size="small"
          sx={{ fontWeight: 'medium' }}
        />
      </Box>

      <Paper
        elevation={0}
        sx={{ bgcolor: 'background.default', p: 2, borderRadius: 1 }}
      >
        <Grid2
          container
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Grid2 sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              src={restockGroup.user.profileImage?.url || ''}
              alt={`${restockGroup.user.firstName} ${restockGroup.user.lastName}`}
              sx={{ width: 40, height: 40 }}
            />
            <Box>
              <Typography variant="body1" fontWeight="medium">
                {restockGroup.user.firstName} {restockGroup.user.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {restockGroup.user.email}
              </Typography>
            </Box>
          </Grid2>
          <Grid2
            sx={{
              color: 'text.secondary',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Clock size={16} />
            <Typography variant="body2">
              {t('restockGroupDetail.completedDatetime', {
                date: timingData.completedTime.toDate(),
              })}
            </Typography>
          </Grid2>
        </Grid2>
      </Paper>
    </Box>
  </Paper>
)

const StatsSection = ({
  restockGroup,
  timingData,
  t,
}: {
  restockGroup: RestockGroupFragment
  timingData: ReturnType<typeof useTimingData>
  t: TFnType<DefaultParamType, string, any>
}) => (
  <Grid2 container spacing={2} sx={{ mb: 3 }}>
    <Grid2 size={{ xs: 12, md: 4 }}>
      <Card>
        <CardContent>
          <Typography variant="h4" component="div" fontWeight="bold">
            {restockGroup.restocks.length}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('restockGroupDetail.displaysRestocked')}
          </Typography>
        </CardContent>
      </Card>
    </Grid2>
    <Grid2 size={{ xs: 12, md: 4 }}>
      <Card>
        <CardContent>
          <Typography variant="h4" component="div" fontWeight="bold">
            {restockGroup.restocks.length * 2}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('restockGroupDetail.totalSnapshots')}
          </Typography>
        </CardContent>
      </Card>
    </Grid2>
    <Grid2 size={{ xs: 12, md: 4 }}>
      <Card>
        <CardContent>
          <Typography variant="h4" component="div" fontWeight="bold">
            {timingData.timeToComplete}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('restockGroupDetail.timeToComplete')}
          </Typography>
        </CardContent>
      </Card>
    </Grid2>
  </Grid2>
)

const RestockStatsCard = ({
  title,
  value,
  delta,
  color,
}: {
  title: string
  value: string
  delta: string
  color?: string
}) => (
  <Grid2 size={{ xs: 12, md: 4 }}>
    <Card sx={{ backgroundColor: 'background.default' }}>
      <CardContent>
        <Typography
          variant="h4"
          component="div"
          color={color}
          fontWeight="bold"
        >
          {value}
          <Typography component="span" variant="body2" color="text.secondary">
            {delta}
          </Typography>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
      </CardContent>
    </Card>
  </Grid2>
)

const RestockCard = ({
  restock,
  t,
  storeId,
  productInfoByUpc,
}: {
  restock: RestockFragment
  t: TFnType<DefaultParamType, string, any>
  storeId?: string
  productInfoByUpc: Record<string, ProductInitialInfo>
}) => {
  const {
    handle: { handleNavTo },
  } = useUrlSearchParams()

  const beforeSnapshot = getSnapshotByType(
    restock.displaySnapshots,
    SnapshotType.PRE_RESTOCK,
  )
  const afterSnapshot = getSnapshotByType(
    restock.displaySnapshots,
    SnapshotType.POST_RESTOCK,
  )

  const snapshots = [beforeSnapshot, afterSnapshot].filter(notNullOrUndefined)
  const stats = calculateRestockStats(beforeSnapshot, afterSnapshot)

  const getStatColor = (delta: number) => {
    if (delta < 0) return 'success.main'
    if (delta === 0) return undefined
    return 'error.main'
  }

  return (
    <Card
      key={restock.id}
      elevation={1}
      sx={{ p: 4, gap: 2, display: 'flex', flexDirection: 'column' }}
    >
      <Box
        sx={{
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h3" fontWeight="semibold">
            {restock.display.name}
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            {storeId && (
              <Link
                onClick={() =>
                  handleNavTo(
                    `/stores/${storeId}/planograms/${restock.display.id}`,
                    {
                      resetParams: false,
                    },
                  )
                }
                sx={{
                  cursor: 'pointer',
                  color: 'error.main',
                  textDecoration: 'none',
                  fontSize: '1rem',
                  fontWeight: 'medium',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                {t('restockGroupDetail.viewPlanogram')}
              </Link>
            )}
            <Typography variant="body2" color="text.secondary">
              {t('restockGroupDetail.restockDatetime', {
                date: dayjs(restock.created).toDate(),
              })}
            </Typography>
          </Stack>
        </Stack>

        <Grid2 container spacing={2}>
          <RestockStatsCard
            title={t('restockGroupDetail.emptyFacings')}
            value={formatStatChange(
              stats.before.emptyFacings,
              stats.after.emptyFacings,
            )}
            delta={formatDelta(stats.deltas.emptyFacings)}
            color={getStatColor(stats.deltas.emptyFacings)}
          />
          <RestockStatsCard
            title={t('restockGroupDetail.identifiedSkus')}
            value={formatStatChange(
              stats.before.identifiedSkus,
              stats.after.identifiedSkus,
            )}
            delta={formatDelta(stats.deltas.identifiedSkus)}
          />
          <RestockStatsCard
            title={t('restockGroupDetail.totalFacings')}
            value={formatStatChange(
              stats.before.totalFacings,
              stats.after.totalFacings,
            )}
            delta={formatDelta(stats.deltas.totalFacings)}
          />
        </Grid2>
      </Box>

      {(!beforeSnapshot || !afterSnapshot) && (
        <Alert severity="error">
          {!beforeSnapshot && !afterSnapshot
            ? t('restockGroupDetail.snapshotsNotFound')
            : !beforeSnapshot
              ? t('restockGroupDetail.beforeSnapshotNotFound')
              : t('restockGroupDetail.afterSnapshotNotFound')}
        </Alert>
      )}

      <Divider />

      <Grid2 container spacing={4}>
        {snapshots.map((snapshot, index) => (
          <RestockSnapshotCard
            key={index}
            snapshot={snapshot}
            productInfoByUpc={productInfoByUpc}
          />
        ))}
      </Grid2>
    </Card>
  )
}

const RestockGroupDetail = () => {
  const { t } = useT()
  const { restockGroupId } = useParams()
  const { data, loading, error } = useRestockGroupData(restockGroupId)

  const timingData = useTimingData(data?.restockGroup)

  const allSnapshotUpcs = useMemo(
    () =>
      (data?.restockGroup?.restocks ?? []).flatMap(restock =>
        (restock.displaySnapshots ?? []).flatMap(s =>
          (s.images?.[0]?.boxSet?.boundingBoxes ?? [])
            .map(b => b.classifiedLabel)
            .filter(notNullOrUndefined),
        ),
      ),
    [data?.restockGroup?.restocks],
  )
  const { productImageByUpc } = useProductsByUpc(allSnapshotUpcs)

  if (loading) {
    return (
      <CenteredBox>
        <CircularProgress />
      </CenteredBox>
    )
  }

  if (!data?.restockGroup) {
    return (
      <CenteredBox>
        <Alert severity="error">
          {error?.message || t('restockGroupDetail.notFound')}
        </Alert>
      </CenteredBox>
    )
  }

  return (
    <Box
      sx={{
        maxWidth: 1200,
        mx: 'auto',
        p: 3,
        minHeight: '100vh',
      }}
    >
      <RestockHeader
        restockGroup={data.restockGroup}
        timingData={timingData}
        t={t}
      />

      <StatsSection
        restockGroup={data.restockGroup}
        timingData={timingData}
        t={t}
      />

      <Box sx={{ mb: 2 }}>
        <Typography variant="h2" fontWeight="semibold" sx={{ mb: 2 }}>
          {t('restockGroupDetail.restockDetails')}
        </Typography>
        <DaiDivider />

        <Stack spacing={3}>
          {cloneDeep(data.restockGroup.restocks)
            .sort((a, b) => {
              return a.display.order - b.display.order
            })
            .map(restock => (
              <RestockCard
                key={restock.id}
                restock={restock}
                t={t}
                storeId={data.restockGroup.store?.id}
                productInfoByUpc={productImageByUpc}
              />
            ))}
        </Stack>
      </Box>
    </Box>
  )
}

export default RestockGroupDetail
